<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ isNew ? $t("app_catalog.btn_add_bundle") : $t("app_catalog.edit_bundle_title", { id: $route.params.id }) }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
                <div class="col-lg-6 col-5 text-right">
                    <!-- Form draft spec 2026-10-07 §3 F2 / §4 A10: create mode
                         only, leftmost of the header group. -->
                    <form-clear-button v-if="isNew" :disabled="saving" @click="clearForm"></form-clear-button>
                    <base-button size="md" type="secondary" @click="$router.back()">
                        <i class="fa fa-arrow-left"></i> {{ $t("back") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <div v-loading="loadingBundle">
                <card>
                    <h4 slot="header" class="mb-0">{{ $t("app_catalog.section_basic_info") }}</h4>
                    <el-form ref="bundleForm" :model="form" :rules="rules" label-width="160px">
                        <el-form-item :label="$t('app_catalog.col_name_th')" prop="name_th">
                            <el-input v-model="form.name_th" style="max-width:420px;"></el-input>
                        </el-form-item>
                        <el-form-item :label="$t('app_catalog.col_name_en')">
                            <el-input v-model="form.name_en" style="max-width:420px;"></el-input>
                        </el-form-item>
                        <el-form-item :label="$t('app_catalog.col_detail')">
                            <vue-editor
                                v-model="form.detail"
                                :editor-toolbar="detailToolbar"
                                use-custom-image-handler
                                @image-added="handleDetailImageAdded"
                            ></vue-editor>
                        </el-form-item>
                        <el-form-item :label="$t('app_catalog.promo_price_mode')">
                            <el-radio-group v-model="form.price_mode">
                                <el-radio label="sum">{{ $t("app_catalog.promo_price_sum") }}</el-radio>
                                <el-radio label="fixed">{{ $t("app_catalog.promo_price_fixed") }}</el-radio>
                            </el-radio-group>
                        </el-form-item>
                        <el-form-item v-if="form.price_mode === 'fixed'" :label="$t('app_catalog.promo_fixed_price')">
                            <el-input-number v-model="form.fixed_price" :min="0" :precision="2" style="width:240px;"></el-input-number>
                        </el-form-item>
                        <el-form-item :label="$t('app_catalog.image_mode_label')">
                            <el-radio-group v-model="form.image_display_mode">
                                <el-radio label="main">{{ $t("app_catalog.image_mode_main") }}</el-radio>
                                <el-radio label="sku">{{ $t("app_catalog.image_mode_sku") }}</el-radio>
                            </el-radio-group>
                            <div class="small text-muted mt-1">{{ $t("app_catalog.image_mode_hint") }}</div>
                        </el-form-item>
                        <el-form-item :label="$t('app_catalog.market_label')">
                            <el-radio-group v-model="form.market">
                                <el-radio label="domestic">{{ $t("app_catalog.market_domestic") }}</el-radio>
                                <el-radio label="international">{{ $t("app_catalog.market_international") }}</el-radio>
                                <el-radio label="both">{{ $t("app_catalog.market_both") }}</el-radio>
                            </el-radio-group>
                            <div class="small text-muted mt-1">{{ $t("app_catalog.market_hint") }}</div>
                        </el-form-item>
                        <el-form-item :label="$t('app_catalog.bonus_points_label')">
                            <el-input-number v-model="form.bonus_points" :min="0" :step="1" step-strictly controls-position="right" />
                            <div class="small text-muted mt-1">{{ $t("app_catalog.bonus_points_hint") }}</div>
                        </el-form-item>
                        <!-- Cash Back % (#654) — sits next to bonus_points because it
                             follows the same 0 = "use the shop value" rule. -->
                        <el-form-item :label="$t('cashback_settings.promo_label')">
                            <el-input-number
                                v-model="form.cashback_percent"
                                :min="0"
                                :max="100"
                                :step="0.5"
                                :precision="2"
                                controls-position="right"
                            />
                            <div class="small text-muted mt-1">{{ $t("cashback_settings.promo_hint") }}</div>
                            <div class="small text-muted mt-1">
                                {{ $t(cashbackEffective.key, { percent: cashbackEffective.percent }) }}
                            </div>
                        </el-form-item>
                        <el-form-item :label="$t('app_catalog.col_date_range')">
                            <el-date-picker
                                v-model="form.dateRange"
                                type="daterange"
                                range-separator="-"
                                :start-placeholder="$t('app_catalog.col_starts_at')"
                                :end-placeholder="$t('app_catalog.col_ends_at')"
                                value-format="yyyy-MM-dd"
                                style="width: 420px; max-width: 100%;"
                            ></el-date-picker>
                        </el-form-item>
                        <el-form-item :label="$t('status')">
                            <el-radio-group v-model="form.status">
                                <el-radio :label="0">{{ $t("app_catalog.status_draft") }}</el-radio>
                                <el-radio :label="1">{{ $t("app_catalog.status_active") }}</el-radio>
                            </el-radio-group>
                        </el-form-item>
                    </el-form>
                </card>

                <!-- Bundle Images gallery -->
                <card class="mt-3">
                    <h4 slot="header" class="mb-0">{{ $t("app_catalog.section_bundle_images") }}</h4>
                    <s3-multi-image-upload
                        object-type="promotion"
                        v-model="form.images"
                        :max="10"
                        large
                    ></s3-multi-image-upload>
                </card>

                <card class="mt-3">
                    <div slot="header" class="d-flex justify-content-between align-items-center">
                        <h4 class="mb-0">{{ $t("app_catalog.slot") }}</h4>
                        <base-button size="sm" type="info" @click="addSlot">
                            <i class="fa fa-plus"></i> {{ $t("app_catalog.add_slot") }}
                        </base-button>
                    </div>

                    <!-- Bulk-apply flash price + window to every option in all slots -->
                    <div
                        class="d-flex align-items-center flex-wrap p-2 mb-3"
                        style="gap: 8px; background: #f8f9fe; border-radius: 6px;"
                    >
                        <span class="small text-muted">{{ $t("app_catalog.bulk_flash_label") }}</span>
                        <el-input-number
                            v-model="bulkFlashPrice"
                            :min="0"
                            :precision="2"
                            :controls="false"
                            size="small"
                            style="width: 120px;"
                            :placeholder="$t('app_catalog.option_flash_price')"
                        ></el-input-number>
                        <el-date-picker
                            v-model="bulkFlashRange"
                            type="datetimerange"
                            range-separator="-"
                            :start-placeholder="$t('app_catalog.col_starts_at')"
                            :end-placeholder="$t('app_catalog.col_ends_at')"
                            value-format="yyyy-MM-dd HH:mm:ss"
                            size="small"
                            style="width: 320px; max-width: 100%;"
                        ></el-date-picker>
                        <base-button size="sm" type="primary" @click="applyFlashToAll">
                            <i class="fa fa-bolt"></i> {{ $t("app_catalog.bulk_flash_apply") }}
                        </base-button>
                    </div>

                    <div v-if="form.slots.length === 0" class="text-danger small mb-2">
                        {{ $t("errors.promotion.no_slots") }}
                    </div>

                    <el-card
                        v-for="(slot, sIdx) in form.slots"
                        :key="sIdx"
                        class="mb-3"
                        shadow="never"
                    >
                        <div slot="header" class="d-flex justify-content-between align-items-center">
                            <div class="d-flex" style="gap: 8px;">
                                <el-input
                                    v-model="slot.label_th"
                                    size="small"
                                    :placeholder="$t('app_catalog.slot_label') + ' (TH)'"
                                    style="width: 220px;"
                                ></el-input>
                                <el-input
                                    v-model="slot.label_en"
                                    size="small"
                                    :placeholder="$t('app_catalog.slot_label') + ' (EN)'"
                                    style="width: 220px;"
                                ></el-input>
                            </div>
                            <base-button size="sm" type="info" class="mr-1" @click="duplicateSlot(sIdx)">
                                <i class="fa fa-copy"></i> {{ $t("app_catalog.duplicate_slot") }}
                            </base-button>
                            <base-button size="sm" type="danger" @click="removeSlot(sIdx)">
                                <i class="fa fa-times"></i>
                            </base-button>
                        </div>

                        <div v-if="slot.options.length === 0" class="text-danger small mb-2">
                            {{ $t("errors.promotion.no_options") }}
                        </div>

                        <!-- Issue #83: widened image column, side-by-side layout; #84: _labelEdited guard on labels -->
                        <el-table :data="slot.options" size="small">
                            <el-table-column :label="$t('app_catalog.option_image')" width="170px">
                                <template slot-scope="opt">
                                    <div class="d-flex align-items-center" style="gap:8px;">
                                        <!-- Thumbnail at full row height; placeholder preserves row height when empty -->
                                        <img
                                            v-if="opt.row.image_url"
                                            :src="resolveImg(opt.row.image_url)"
                                            style="width:56px; height:56px; object-fit:contain; background:#f5f5f5; border-radius:4px; flex-shrink:0;"
                                            @error="$event.target.style.display = 'none'"
                                        />
                                        <div
                                            v-else
                                            style="width:56px; height:56px; background:#f0f0f0; border-radius:4px; flex-shrink:0;"
                                        ></div>
                                        <!-- Buttons to the RIGHT of the thumbnail -->
                                        <div class="d-flex flex-column" style="gap:4px;">
                                            <base-button
                                                size="sm"
                                                type="secondary"
                                                style="padding: 2px 6px;"
                                                @click="triggerOptionImage(sIdx, opt.$index)"
                                            >
                                                <i class="fa fa-upload"></i>
                                                {{ opt.row.image_url ? $t("app_catalog.option_change_image") : $t("app_catalog.option_upload_image") }}
                                            </base-button>
                                            <base-button
                                                v-if="opt.row.image_url"
                                                size="sm"
                                                type="danger"
                                                style="padding: 2px 6px;"
                                                @click="opt.row.image_url = ''"
                                            >
                                                <i class="fa fa-times"></i>
                                            </base-button>
                                        </div>
                                    </div>
                                </template>
                            </el-table-column>
                            <el-table-column :label="$t('app_catalog.option')" min-width="180px">
                                <template slot-scope="opt">
                                    <el-select
                                        v-model="opt.row.ref_id"
                                        filterable
                                        remote
                                        :remote-method="(q) => searchRefOptions(q, sIdx)"
                                        :loading="refSearchLoading"
                                        :placeholder="$t('app_catalog.option_label')"
                                        class="w-100"
                                        :no-data-text="optionNoDataText"
                                        @change="(val) => onRefSelected(val, sIdx, opt.$index)"
                                        @visible-change="(v) => onOptionDropdown(v, sIdx)"
                                    >
                                        <el-option
                                            v-for="r in optionsForRow(sIdx, opt.row)"
                                            :key="r.value"
                                            :label="r.label"
                                            :value="r.value"
                                        ></el-option>
                                    </el-select>
                                    <!-- #84: @input sets _labelEdited so WMS pick never overwrites after user edits -->
                                    <el-input
                                        v-model="opt.row.label_th"
                                        size="mini"
                                        class="mt-1"
                                        :placeholder="$t('app_catalog.option_display_name') + ' (TH)'"
                                        @input="opt.row._labelEdited = true"
                                    ></el-input>
                                    <el-input
                                        v-model="opt.row.label_en"
                                        size="mini"
                                        class="mt-1"
                                        :placeholder="$t('app_catalog.option_display_name') + ' (EN)'"
                                        @input="opt.row._labelEdited = true"
                                    ></el-input>
                                </template>
                            </el-table-column>
                            <el-table-column v-if="form.price_mode !== 'fixed'" :label="$t('app_catalog.option_price')" width="130px">
                                <template slot-scope="opt">
                                    <el-input-number
                                        v-model="opt.row.price"
                                        :min="0"
                                        :precision="2"
                                        :controls="false"
                                        size="small"
                                        style="width:100%"
                                    ></el-input-number>
                                </template>
                            </el-table-column>
                            <el-table-column :label="$t('app_catalog.option_flash_price')" width="150px">
                                <template slot-scope="opt">
                                    <el-input-number
                                        v-model="opt.row.flash_price"
                                        :min="0"
                                        :precision="2"
                                        :controls="false"
                                        size="small"
                                        style="width:100%"
                                        :placeholder="$t('app_catalog.option_flash_price')"
                                    ></el-input-number>
                                </template>
                            </el-table-column>
                            <el-table-column :label="$t('app_catalog.option_flash_window')" width="340px">
                                <template slot-scope="opt">
                                    <el-date-picker
                                        v-model="opt.row.flashRange"
                                        type="datetimerange"
                                        range-separator="-"
                                        :start-placeholder="$t('app_catalog.col_starts_at')"
                                        :end-placeholder="$t('app_catalog.col_ends_at')"
                                        value-format="yyyy-MM-dd HH:mm:ss"
                                        size="small"
                                        style="width: 320px; max-width: 100%;"
                                    ></el-date-picker>
                                </template>
                            </el-table-column>
                            <el-table-column :label="$t('actions')" width="90px" align="center">
                                <template slot-scope="opt">
                                    <base-button size="sm" type="danger" @click="removeOption(sIdx, opt.$index)">
                                        <i class="fa fa-times"></i>
                                    </base-button>
                                </template>
                            </el-table-column>
                        </el-table>

                        <base-button size="sm" type="secondary" class="mt-2" @click="addOption(sIdx)">
                            <i class="fa fa-plus"></i> {{ $t("app_catalog.add_option") }}
                        </base-button>
                    </el-card>
                </card>

                <!-- Issue #86: Giveaway ("ของแถม") items card -->
                <card class="mt-3">
                    <div slot="header" class="d-flex justify-content-between align-items-center">
                        <h4 class="mb-0">{{ $t("app_catalog.section_giveaways") }}</h4>
                        <base-button size="sm" type="info" @click="addGiveaway">
                            <i class="fa fa-plus"></i> {{ $t("app_catalog.add_giveaway") }}
                        </base-button>
                    </div>

                    <div v-if="form.giveaways.length === 0" class="text-muted small mb-2">
                        {{ $t("app_catalog.no_data") }}
                    </div>

                    <el-table v-if="form.giveaways.length > 0" :data="form.giveaways" size="small">
                        <el-table-column :label="$t('app_catalog.option_image')" width="170px">
                            <template slot-scope="gift">
                                <div class="d-flex align-items-center" style="gap:8px;">
                                    <img
                                        v-if="gift.row.image_url"
                                        :src="resolveImg(gift.row.image_url)"
                                        style="width:56px; height:56px; object-fit:contain; background:#f5f5f5; border-radius:4px; flex-shrink:0;"
                                        @error="$event.target.style.display = 'none'"
                                    />
                                    <div
                                        v-else
                                        style="width:56px; height:56px; background:#f0f0f0; border-radius:4px; flex-shrink:0;"
                                    ></div>
                                    <div class="d-flex flex-column" style="gap:4px;">
                                        <base-button
                                            size="sm"
                                            type="secondary"
                                            style="padding: 2px 6px;"
                                            @click="triggerGiveawayImage(gift.$index)"
                                        >
                                            <i class="fa fa-upload"></i>
                                            {{ gift.row.image_url ? $t("app_catalog.option_change_image") : $t("app_catalog.option_upload_image") }}
                                        </base-button>
                                        <base-button
                                            v-if="gift.row.image_url"
                                            size="sm"
                                            type="danger"
                                            style="padding: 2px 6px;"
                                            @click="gift.row.image_url = ''"
                                        >
                                            <i class="fa fa-times"></i>
                                        </base-button>
                                    </div>
                                </div>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.option')" min-width="180px">
                            <template slot-scope="gift">
                                <el-select
                                    v-model="gift.row.ref_id"
                                    filterable
                                    remote
                                    :remote-method="(q) => searchRefOptions(q, 'gift')"
                                    :loading="refSearchLoading"
                                    :placeholder="$t('app_catalog.option_label')"
                                    class="w-100"
                                    :no-data-text="optionNoDataText"
                                    @change="(val) => onGiveawayRefSelected(val, gift.$index)"
                                    @visible-change="(v) => onGiveawayDropdown(v)"
                                >
                                    <el-option
                                        v-for="r in optionsForGiveaway(gift.row)"
                                        :key="r.value"
                                        :label="r.label"
                                        :value="r.value"
                                    ></el-option>
                                </el-select>
                                <el-input
                                    v-model="gift.row.label_th"
                                    size="mini"
                                    class="mt-1"
                                    :placeholder="$t('app_catalog.option_display_name') + ' (TH)'"
                                    @input="gift.row._labelEdited = true"
                                ></el-input>
                                <el-input
                                    v-model="gift.row.label_en"
                                    size="mini"
                                    class="mt-1"
                                    :placeholder="$t('app_catalog.option_display_name') + ' (EN)'"
                                    @input="gift.row._labelEdited = true"
                                ></el-input>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.giveaway_qty')" width="150px">
                            <template slot-scope="gift">
                                <el-input-number
                                    v-model="gift.row.quantity"
                                    :min="1"
                                    :step="1"
                                    :precision="0"
                                    :controls="false"
                                    size="small"
                                    style="width:100%"
                                ></el-input-number>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('actions')" width="90px" align="center">
                            <template slot-scope="gift">
                                <base-button size="sm" type="danger" @click="removeGiveaway(gift.$index)">
                                    <i class="fa fa-times"></i>
                                </base-button>
                            </template>
                        </el-table-column>
                    </el-table>
                </card>

                <div class="text-right mt-3 mb-4">
                    <base-button type="secondary" class="mr-2" @click="$router.back()">{{ $t("cancel") || "Cancel" }}</base-button>
                    <base-button type="primary" :loading="saving" @click="submitForm">{{ $t("save") }}</base-button>
                </div>
            </div>
        </div>

        <!-- Shared hidden file input for per-option single-image upload. -->
        <input
            ref="optionImageInput"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style="display: none;"
            @change="onOptionImageChange"
        />
    </div>
</template>

<script>
import { apiErrorMessage } from "@/util/api-error";
import { mapGetters, mapActions } from "vuex";
import { requestRefresh } from "@/utils/pageRefresh";
import formDraft from "@/mixins/formDraft";
import FormClearButton from "@/components/FormClearButton.vue";
import {
    Form,
    FormItem,
    Input,
    InputNumber,
    Select,
    Option,
    DatePicker,
    RadioGroup,
    Radio,
    Table,
    TableColumn,
    Card
} from "element-ui";
import S3MultiImageUpload from "@/components/Inputs/S3MultiImageUpload.vue";
import { VueEditor } from "vue2-editor";
import { AppCatalogUploadService, StorefrontService } from "../../resource";
import { normalizeCashbackPercent, cashbackEffectiveLabel, CASHBACK_DEFAULT_PERCENT } from "@/utils/cashbackConfig";
// CDN image URLs are signed; the resolve rule is pure + unit-tested in
// tests/unit/cdnImage.spec.js.
import { cdnImageUrl } from "@/utils/cdnImage";

export default {
    name: "AppCatalogPromotionBundleEdit",
    // Form draft (spec 2026-10-07 §3/§4 A10): create mode keeps its draft while
    // the tab is cached, clears on "ล้างฟอร์ม" and after a successful create.
    mixins: [formDraft],
    components: {
        FormClearButton,
        S3MultiImageUpload,
        VueEditor,
        [Form.name]: Form,
        [FormItem.name]: FormItem,
        [Input.name]: Input,
        [InputNumber.name]: InputNumber,
        [Select.name]: Select,
        [Option.name]: Option,
        [DatePicker.name]: DatePicker,
        [RadioGroup.name]: RadioGroup,
        [Radio.name]: Radio,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Card.name]: Card
    },
    data() {
        return {
            saving: false,
            loadingBundle: false,
            refSearchLoading: false,
            // Per-slot remote-search results, keyed by slot index.
            refResults: {},
            // Toolbar shared with ProductEdit's rich-text detail editor.
            detailToolbar: [
                [{ header: [false, 1, 2, 3] }],
                ["bold", "italic", "underline", "strike"],
                [{ color: [] }, { background: [] }],
                [{ list: "ordered" }, { list: "bullet" }],
                [{ align: [] }],
                ["link", "image"],
                ["clean"]
            ],
            // Which option the hidden file input is currently targeting.
            // sIdx="gift" + oIdx=giftIdx when targeting a giveaway row.
            optionImageTarget: { sIdx: null, oIdx: null },
            // Bulk-apply flash controls (applied to every option in all slots).
            bulkFlashPrice: undefined,
            bulkFlashRange: null,
            form: {
                kind: "wms_product",
                name_th: "",
                name_en: "",
                detail: "",
                price_mode: "sum",
                fixed_price: 0,
                image_display_mode: "main",
                market: "domestic",
                bonus_points: 0,
                // Cash Back % (#654). 0 = "no opinion" — the shop default applies.
                // Above 0 REPLACES the shop default for this bundle; it is never
                // added on top. Same overload bonus_points above already carries.
                cashback_percent: 0,
                status: 0,
                dateRange: null,
                images: [],
                slots: [],
                giveaways: []
            },
            // The shop-level cashback config, read once so the hint under the
            // field can say what percentage ACTUALLY applies. Read-only here —
            // this screen never writes it (that is StorefrontSettings.vue).
            cashbackShop: { enabled: false, default_percent: CASHBACK_DEFAULT_PERCENT },
            rules: {
                name_th: [{ required: true, message: this.$t("app_catalog.field_required"), trigger: "blur" }]
            }
        };
    },
    computed: {
        isNew() {
            return this.$route.params.id === "new";
        },
        optionNoDataText() {
            return this.$t("app_catalog.no_data");
        },
        // { key, percent } for the "what actually applies" hint (#654).
        cashbackEffective() {
            return cashbackEffectiveLabel(
                this.cashbackShop.enabled,
                this.cashbackShop.default_percent,
                this.form.cashback_percent
            );
        },
        ...mapGetters({ isAdmin: "Auth/isAdmin" })
    },
    mounted() {
        this.loadCashbackShopConfig();
        if (!this.isNew) {
            this.loadBundle();
        }
    },
    methods: {
        // ---- Form draft (spec 2026-10-07 §3 F1 / §4 A10) ----
        // Back to a blank NEW bundle: every field of data() (names, detail,
        // price mode, image mode, market, points, cashback, status, dates,
        // gallery, slots + options, giveaways, the bulk-flash bar, picker
        // search results, upload target), the hidden option-image input and
        // the el-form validation marks. The shop cashback config is a lookup
        // read once on mount, not form data — it survives. Edit mode never resets.
        resetForm() {
            const fresh = this.$options.data.call(this);
            delete fresh.cashbackShop;
            Object.assign(this.$data, fresh);
            const input = this.$refs && this.$refs.optionImageInput;
            if (input) input.value = "";
            this.$nextTick(() => {
                const form = this.$refs && this.$refs.bundleForm;
                if (form && typeof form.clearValidate === "function") form.clearValidate();
            });
        },
        ...mapActions({
            fetchOne: "AppCatalogPromotionBundle/fetchOne",
            createItem: "AppCatalogPromotionBundle/create",
            updateItem: "AppCatalogPromotionBundle/update",
            searchWmsProducts: "AppCatalogPromotionBundle/searchWmsProducts"
        }),
        // #654: read the shop-level cashback config so the hint under the
        // Cash Back % field can say what actually applies. A failure here is
        // SILENT on purpose — it only degrades a hint, and a red toast about
        // cashback while the operator is editing slots would be noise.
        async loadCashbackShopConfig() {
            try {
                const res = await StorefrontService.getCashbackSettings();
                const data = (res.data && res.data.data) || {};
                const pct = normalizeCashbackPercent(data.default_percent);
                this.cashbackShop = {
                    enabled: data.enabled === true,
                    default_percent: pct === null ? CASHBACK_DEFAULT_PERCENT : pct
                };
            } catch (err) {
                this.cashbackShop = { enabled: false, default_percent: CASHBACK_DEFAULT_PERCENT };
            }
        },
        async loadBundle() {
            this.loadingBundle = true;
            try {
                await this.fetchOne(this.$route.params.id);
                const b = this.$store.getters["AppCatalogPromotionBundle/current"];
                if (b) {
                    // Map bundle gallery: BO GET returns objects { object_key, cdn_url } or CDN URL strings.
                    // Normalise to { cdn_url, object_key, sort_order } for S3MultiImageUpload.
                    const rawImages = Array.isArray(b.images) ? b.images : [];
                    const mappedImages = rawImages.map((img, i) => {
                        if (typeof img === "string") {
                            return { cdn_url: img, object_key: "", sort_order: i };
                        }
                        return {
                            cdn_url: img.cdn_url || img.url || "",
                            object_key: img.object_key || "",
                            sort_order: img.sort_order !== undefined ? img.sort_order : i
                        };
                    });
                    this.form = {
                        kind: "wms_product",
                        name_th: b.name_th || "",
                        name_en: b.name_en || "",
                        detail: b.detail || "",
                        price_mode: b.price_mode || "sum",
                        fixed_price: b.fixed_price || 0,
                        image_display_mode: b.image_display_mode || "main",
                        market: b.market || "domestic",
                        bonus_points: b.bonus_points || 0,
                        // #654: an API that predates the column (or a database
                        // that has not been migrated) answers 0, which is also
                        // the "use the shop default" value — so a missing field
                        // and an unset override look the same, on purpose.
                        cashback_percent: normalizeCashbackPercent(b.cashback_percent) || 0,
                        status: b.status || 0,
                        dateRange: b.starts_at && b.ends_at
                            ? [(this.toPickerLocal(b.starts_at) || "").slice(0, 10), (this.toPickerLocal(b.ends_at) || "").slice(0, 10)]
                            : null,
                        images: mappedImages,
                        slots: (b.slots || []).map(s => ({
                            label_th: s.label_th || "",
                            label_en: s.label_en || "",
                            sort_order: s.sort_order || 0,
                            options: (s.options || []).map(o => ({
                                ref_kind: "wms_product",
                                ref_id: o.ref_id,
                                label_th: o.label_th || "",
                                label_en: o.label_en || "",
                                // #84: seed _refName as fallback display in picker;
                                // _labelEdited=true so WMS pick never overwrites saved label.
                                _refName: o.label_th || o.label_en || String(o.ref_id),
                                _labelEdited: !!(o.label_th || o.label_en),
                                image_url: o.image_url || "",
                                price: (o.price === null || o.price === undefined) ? undefined : o.price,
                                flash_price: (o.flash_price === null || o.flash_price === undefined) ? undefined : o.flash_price,
                                flashRange: (o.flash_starts_at && o.flash_ends_at)
                                    ? [this.toPickerLocal(o.flash_starts_at), this.toPickerLocal(o.flash_ends_at)]
                                    : null,
                                sort_order: o.sort_order || 0
                            }))
                        })),
                        // #86: load giveaways from the BE top-level giveaways array.
                        giveaways: (b.giveaways || []).map(g => ({
                            ref_kind: "wms_product",
                            ref_id: g.ref_id,
                            label_th: g.label_th || "",
                            label_en: g.label_en || "",
                            _refName: g.label_th || g.label_en || String(g.ref_id),
                            _labelEdited: !!(g.label_th || g.label_en),
                            image_url: g.image_url || "",
                            quantity: g.quantity || 1
                        }))
                    };
                }
            } finally {
                this.loadingBundle = false;
            }
        },
        addSlot() {
            this.form.slots.push({ label_th: "", label_en: "", sort_order: this.form.slots.length, options: [] });
        },
        removeSlot(idx) {
            this.form.slots.splice(idx, 1);
        },
        applyFlashToAll() {
            // Require at least one of price/window so an accidental click can't wipe all flash.
            const hasRange = Array.isArray(this.bulkFlashRange) && this.bulkFlashRange[0] && this.bulkFlashRange[1];
            if ((this.bulkFlashPrice === undefined || this.bulkFlashPrice === null || this.bulkFlashPrice === "") && !hasRange) {
                this.$notify({ type: "warning", message: this.$t("app_catalog.bulk_flash_empty") });
                return;
            }
            let count = 0;
            this.form.slots.forEach(s => {
                (s.options || []).forEach(o => {
                    o.flash_price = this.bulkFlashPrice;
                    // Give each option its OWN range array instance (no shared reference).
                    o.flashRange = hasRange ? this.bulkFlashRange.slice() : null;
                    count += 1;
                });
            });
            this.$notify({ type: "success", message: this.$t("app_catalog.bulk_flash_applied", { count }) });
        },
        duplicateSlot(sIdx) {
            // Deep-clone via JSON round-trip (all slot/option fields are plain serialisable:
            // strings, numbers, null, boolean, arrays — no File objects or functions reach here;
            // File objects only live transiently in onOptionImageChange and are never stored
            // on form.slots). id fields are intentionally absent from the form shape (stripped
            // in loadBundle); strip defensively in case any are present on the source.
            const raw = JSON.parse(JSON.stringify(this.form.slots[sIdx]));
            delete raw.id;
            raw.options = raw.options.map(o => {
                const clone = Object.assign({}, o);
                delete clone.id;
                // _labelEdited and _refName are preserved so duplicated custom labels are not
                // re-seeded from the WMS product name when onRefSelected is called.
                return clone;
            });
            // Insert immediately after the source slot. Vue will re-key the v-for by sIdx,
            // so existing refResults entries shift — clear the results map entirely to avoid
            // stale picker state for any slot index. Each slot's dropdown re-loads on open
            // via onOptionDropdown -> searchRefOptions, so clearing is safe.
            this.refResults = {};
            this.form.slots.splice(sIdx + 1, 0, raw);
        },
        addOption(sIdx) {
            this.form.slots[sIdx].options.push({
                ref_kind: this.form.kind,
                ref_id: null,
                label_th: "",
                label_en: "",
                // #84: new options start with _labelEdited=false so first pick auto-fills.
                _refName: "",
                _labelEdited: false,
                image_url: "",
                price: undefined,
                flash_price: undefined,
                flashRange: null,
                sort_order: this.form.slots[sIdx].options.length
            });
        },
        removeOption(sIdx, oIdx) {
            this.form.slots[sIdx].options.splice(oIdx, 1);
        },
        refResultsFor(sIdx) {
            return this.refResults[sIdx] || [];
        },
        // The slot-shared `refResults` is replaced whenever the admin searches one
        // row's dropdown, which would blank out the other rows' (and the initial
        // saved) selections — el-select shows the raw ref_id when no matching
        // el-option exists. So for each row we prepend a synthetic option for the
        // currently-selected ref_id (labelled with its display name) when the live
        // search results don't already contain it. This makes a saved bundle render
        // the option NAME on edit instead of the bare id, regardless of search state.
        optionsForRow(sIdx, row) {
            const results = this.refResults[sIdx] || [];
            if (row.ref_id === null || row.ref_id === undefined || row.ref_id === "") {
                return results;
            }
            if (results.some(r => r.value === row.ref_id)) {
                return results;
            }
            // #84: use _refName (WMS product name) for the picker display,
            // NOT label_th which is the staff-edited customer-facing label.
            const name = row._refName || String(row.ref_id);
            return [{ value: row.ref_id, label: name }, ...results];
        },
        // Giveaway picker options (keyed under "gift" in refResults).
        optionsForGiveaway(row) {
            return this.optionsForRow("gift", row);
        },
        // Preload the dropdowns on open so a click shows a list (remote selects
        // otherwise stay empty until the user types).
        onOptionDropdown(visible, sIdx) {
            if (visible) this.searchRefOptions("", sIdx);
        },
        async searchRefOptions(query, sIdx) {
            this.refSearchLoading = true;
            try {
                const res = await this.searchWmsProducts({ q: query, limit: 20 });
                const rows = (res.data && res.data.data) || [];
                const results = rows.map(w => ({
                    value: w.id,
                    label: `${w.name}${w.barcode ? " / " + w.barcode : ""}`,
                    label_th: w.name,
                    label_en: w.name,
                    image_url: w.image || "",
                    price: w.cost || 0
                }));
                this.$set(this.refResults, sIdx, results);
            } finally {
                this.refSearchLoading = false;
            }
        },
        onRefSelected(refId, sIdx, oIdx) {
            const results = this.refResults[sIdx] || [];
            const picked = results.find(r => r.value === refId);
            if (!picked) return;
            const opt = this.form.slots[sIdx].options[oIdx];
            opt.ref_kind = this.form.kind;
            // #84: cache WMS name for the picker display label (decoupled from editable label).
            this.$set(opt, "_refName", picked.label_th || picked.label || String(refId));
            // #84: seed label only on first pick (when staff has not yet typed anything).
            // Use _labelEdited flag instead of emptiness check — once staff edits,
            // re-picking any WMS product does NOT overwrite their label.
            if (!opt._labelEdited) {
                opt.label_th = picked.label_th || "";
                opt.label_en = picked.label_en || "";
            }
        },
        onGiveawayRefSelected(refId, gIdx) {
            const results = this.refResults["gift"] || [];
            const picked = results.find(r => r.value === refId);
            if (!picked) return;
            const gift = this.form.giveaways[gIdx];
            gift.ref_kind = "wms_product";
            this.$set(gift, "_refName", picked.label_th || picked.label || String(refId));
            if (!gift._labelEdited) {
                gift.label_th = picked.label_th || "";
                gift.label_en = picked.label_en || "";
            }
        },
        onGiveawayDropdown(visible) {
            if (visible) this.searchRefOptions("", "gift");
        },
        // #86: giveaway CRUD helpers.
        addGiveaway() {
            this.form.giveaways.push({
                ref_kind: "wms_product",
                ref_id: null,
                label_th: "",
                label_en: "",
                _refName: "",
                _labelEdited: false,
                image_url: "",
                quantity: 1
            });
        },
        removeGiveaway(gIdx) {
            this.form.giveaways.splice(gIdx, 1);
        },
        triggerGiveawayImage(gIdx) {
            // Use sIdx="gift" sentinel to indicate giveaway row.
            this.optionImageTarget = { sIdx: "gift", oIdx: gIdx };
            this.$refs.optionImageInput.click();
        },
        // #69: per-option single-image upload (presign -> PUT to S3 -> write the
        // option's image_url). Reuses the same S3 presign flow as the gallery.
        triggerOptionImage(sIdx, oIdx) {
            this.optionImageTarget = { sIdx, oIdx };
            this.$refs.optionImageInput.click();
        },
        async onOptionImageChange(e) {
            const file = e.target.files && e.target.files[0];
            e.target.value = "";
            if (!file) return;
            const { sIdx, oIdx } = this.optionImageTarget;
            if (sIdx === null || oIdx === null) return;
            // Resolve target row: giveaway rows use sIdx="gift".
            let opt;
            if (sIdx === "gift") {
                opt = this.form.giveaways[oIdx];
            } else {
                opt = this.form.slots[sIdx] && this.form.slots[sIdx].options[oIdx];
            }
            if (!opt) return;
            const maxBytes = 5 * 1024 * 1024;
            if (file.size > maxBytes) {
                this.$notify({ type: "danger", message: this.$t("errors.upload.file_too_large") || "File too large (max 5MB)" });
                return;
            }
            try {
                const presignRes = await AppCatalogUploadService.presign({
                    object_type: "promotion",
                    file_name: file.name,
                    content_type: file.type
                });
                const { upload_url, cdn_url } = presignRes.data;
                await new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open("PUT", upload_url);
                    xhr.setRequestHeader("Content-Type", file.type);
                    xhr.onload = () => {
                        if (xhr.status >= 200 && xhr.status < 300) resolve();
                        else reject(new Error("Upload failed: " + xhr.status));
                    };
                    xhr.onerror = () => reject(new Error("Network error"));
                    xhr.send(file);
                });
                // Manual upload OVERRIDES the ref auto-fill.
                opt.image_url = cdn_url;
            } catch (err) {
                const detail = (err && err.message) ? ": " + err.message : "";
                this.$notify({ type: "danger", message: (this.$t("errors.upload.presign_failed") || "Upload failed") + detail });
            }
        },
        // #69: rich-text detail image upload (same as ProductEdit) — presign,
        // PUT to S3, insert the CDN URL into the editor HTML.
        async handleDetailImageAdded(file, Editor, cursorLocation, resetUploader) {
            try {
                const presignRes = await AppCatalogUploadService.presign({
                    object_type: "promotion",
                    file_name: file.name,
                    content_type: file.type
                });
                const { upload_url, cdn_url } = presignRes.data;
                await new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open("PUT", upload_url);
                    xhr.setRequestHeader("Content-Type", file.type);
                    xhr.onload = () => {
                        if (xhr.status >= 200 && xhr.status < 300) resolve();
                        else reject(new Error("Upload failed: " + xhr.status));
                    };
                    xhr.onerror = () => reject(new Error("Network error"));
                    xhr.send(file);
                });
                Editor.insertEmbed(cursorLocation, "image", cdn_url);
                resetUploader();
            } catch (err) {
                const detail = (err && err.message) ? ": " + err.message : "";
                this.$notify({ type: "danger", message: (this.$t("errors.upload.presign_failed") || "Upload failed") + detail });
                resetUploader();
            }
        },
        // Resolve an image for display. The rule lives in @/utils/cdnImage (same
        // helper as AppCatalog/Products.vue): a backend-resolved URL is rendered
        // byte for byte — its `?e=&s=&k=` signature is what gets it past the CDN
        // Worker — the legacy private direct-S3 origin is swapped for the CDN
        // origin with path+query intact, and a bare object key resolves to ""
        // rather than to an unsigned URL this page has no way to sign.
        // Signed-image spec docs/specs/2026-10-03-signed-image-url-design.md §6.4:
        // the payload already carries a resolved URL (Catalog_promotion_bundle.go:1136).
        resolveImg(url) {
            return cdnImageUrl(url);
        },
        validateTree() {
            if (this.form.slots.length === 0) {
                this.$notify({ type: "danger", message: this.$t("errors.promotion.no_slots") });
                return false;
            }
            for (const s of this.form.slots) {
                if (s.options.length === 0) {
                    this.$notify({ type: "danger", message: this.$t("errors.promotion.no_options") });
                    return false;
                }
                for (const o of s.options) {
                    if (!o.ref_id) {
                        this.$notify({ type: "danger", message: this.$t("errors.promotion.invalid_ref") });
                        return false;
                    }
                }
            }
            return true;
        },
        submitForm() {
            this.$refs.bundleForm.validate(async (valid) => {
                if (!valid) return;
                if (!this.validateTree()) return;

                const startsLocal = this.form.dateRange ? this.form.dateRange[0] : null;
                const endsLocal = this.form.dateRange ? this.form.dateRange[1] : null;
                if (startsLocal && endsLocal && endsLocal < startsLocal) {
                    this.$notify({ type: "danger", message: this.$t("errors.promotion.invalid_date_range") });
                    return;
                }

                this.saving = true;
                try {
                    const imageList = Array.isArray(this.form.images) ? this.form.images : [];
                    const payload = {
                        kind: "wms_product",
                        name_th: this.form.name_th,
                        name_en: this.form.name_en,
                        detail: this.form.detail,
                        price_mode: this.form.price_mode,
                        fixed_price: this.form.fixed_price,
                        image_display_mode: this.form.image_display_mode || "main",
                        market: this.form.market || "domestic",
                        bonus_points: Number(this.form.bonus_points) || 0,
                        // #654: always sent. An empty box (null) means "no
                        // override", which is 0 — not "leave whatever is stored",
                        // because the operator cleared it deliberately.
                        cashback_percent: normalizeCashbackPercent(this.form.cashback_percent) || 0,
                        status: this.form.status,
                        // daterange emits date-only (yyyy-MM-dd); span the full day:
                        // start at 00:00:00, end at 23:59:59, then convert to RFC3339 instant.
                        starts_at: startsLocal ? new Date(startsLocal + "T00:00:00").toISOString() : null,
                        ends_at: endsLocal ? new Date(endsLocal + "T23:59:59").toISOString() : null,
                        image_keys: imageList
                            .filter(img => img && typeof img === "object" && img.object_key)
                            .map(img => img.object_key),
                        slots: this.form.slots.map((s, si) => ({
                            label_th: s.label_th,
                            label_en: s.label_en,
                            sort_order: si,
                            options: s.options.map((o, oi) => ({
                                ref_kind: "wms_product",
                                ref_id: o.ref_id,
                                label_th: o.label_th,
                                label_en: o.label_en,
                                // #84: _labelEdited and _refName are FE-only — not included.
                                image_url: o.image_url,
                                price: (o.price === null || o.price === undefined || o.price === "") ? 0 : Number(o.price),
                                // #60: per-option flash window. flash_price null => no flash.
                                flash_price: (o.flash_price === null || o.flash_price === undefined || o.flash_price === "")
                                    ? null
                                    : Number(o.flash_price),
                                flash_starts_at: (o.flashRange && o.flashRange[0])
                                    ? new Date(o.flashRange[0]).toISOString()
                                    : null,
                                flash_ends_at: (o.flashRange && o.flashRange[1])
                                    ? new Date(o.flashRange[1]).toISOString()
                                    : null,
                                sort_order: oi
                            }))
                        })),
                        // #86: giveaway items — flat list, price always 0, is_giveaway set by BE.
                        giveaways: this.form.giveaways.map(g => ({
                            ref_kind: "wms_product",
                            ref_id: g.ref_id,
                            label_th: g.label_th,
                            label_en: g.label_en,
                            image_url: g.image_url,
                            quantity: Number(g.quantity) || 1
                        }))
                    };

                    const wasNew = this.isNew;
                    if (wasNew) {
                        await this.createItem(payload);
                    } else {
                        await this.updateItem({ id: this.$route.params.id, payload });
                    }
                    this.$notify({ type: "success", message: this.$t("app_catalog.save_success") });
                    // Form draft §4 A10: empty the cached /new tab BEFORE leaving.
                    // Edit mode keeps what was just saved.
                    if (wasNew) this.afterSaveReset();
                    requestRefresh("/seller/bundles");
                    this.$router.push("/seller/bundles");
                } catch (err) {
                    const msg = apiErrorMessage(err, this.$t);
                    this.$notify({ type: "danger", message: msg });
                } finally {
                    this.saving = false;
                }
            });
        },
        toPickerLocal(dateStr) {
            if (!dateStr) return null;
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return null;
            const pad = n => String(n).padStart(2, "0");
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        }
    }
};
</script>
